/* eslint-disable max-lines */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faX } from '@fortawesome/pro-solid-svg-icons';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { getTimeDisplayFromDate } from '@/utils';
import { useState } from 'react';
import { Button, Textarea } from '@e2n/ui';
import { useParams } from 'react-router-dom';
import { useAntraegeUpdate } from '../../../../hooks/useAntraegeUpdate';
import { ArbeitszeitArbeitszeitantragStatus } from '@/gql/graphql';
import { Antrag } from '@/domain/arbeitszeiten/entities';
import { AntragInfoText } from './AntragInfoText';
import dayjs from 'dayjs';
import { useProfile } from '@/hooks/useProfile';
import { AntragVerlauf } from './AntragVerlauf';
import { renderAvatar } from './renderAvatar';
import { useManagerCanEditArbeitszeiten } from '@/domain/arbeitszeiten/hooks/useManagerCanEditArbeitszeit';
import { TimeInput } from '../../../TimeInput';
import { toast } from '@/hooks/use-toast';

type AntragFormProps = {
  currentAntrag: Antrag | undefined;
  antraege?: Antrag[];
  beginnAZ?: Date;
  endeAZ?: Date;
};

export function AntragForm({
  currentAntrag,
  antraege = [],
  beginnAZ,
  endeAZ,
}: AntragFormProps) {
  const {
    ablehnen,
    genehmigen,
    loading: antraegeLoading,
  } = useAntraegeUpdate();
  const canEditArbeitszeit = useManagerCanEditArbeitszeiten();

  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);

  const { year, month, day } = useParams();
  const date = new Date(`${year}-${month}-${day}`);

  const { manager } = useProfile();

  const initialsVorgesetzter =
    manager.vorname[0].toUpperCase() + manager.nachname[0].toUpperCase();
  const { arbeitszeitId } = useParams();

  const form = useForm<{
    beginn: string | null;
    ende: string | null;
    date: Date | null;
    bemerkungAntragsteller: string | null;
    bemerkungVorgesetzter: string | null;
  }>({
    mode: 'onBlur',
    defaultValues: {
      beginn: currentAntrag?.beginn?.time
        ? getTimeDisplayFromDate(new Date(currentAntrag.beginn.time))
        : beginnAZ
          ? getTimeDisplayFromDate(beginnAZ)
          : null,
      ende: currentAntrag?.ende?.time
        ? getTimeDisplayFromDate(new Date(currentAntrag.ende.time))
        : endeAZ
          ? getTimeDisplayFromDate(endeAZ)
          : null,
      date: date ?? null,
      bemerkungAntragsteller: currentAntrag?.bemerkungAntragsteller ?? '',
      bemerkungVorgesetzter: currentAntrag?.bemerkungVorgesetzter ?? '',
    },
  });

  if (!currentAntrag) {
    return (
      <div className="text-center text-sm text-neutrals-700">
        {t('domain.arbeitszeiten.antrag.keineAntraegeVorhanden')}
      </div>
    );
  }

  async function handleSubmit(beginn: string, ende: string | null) {
    if (!beginn || beginn.length === 0) {
      toast({
        title: t('domain.arbeitszeiten.sheet.arbeitszeit.edit.fehler'),
      });
      return;
    }
    const formatTime = (date: Date) =>
      `${date.getHours().toString().padStart(2, '0')}:${date
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;

    if (
      beginn === formatTime(new Date(currentAntrag.beginn.time)) &&
      ende === formatTime(new Date(currentAntrag.ende.time))
    ) {
      if (arbeitszeitId) {
        handleApprove();
      } else {
        handleReject();
      }
    }
  }

  async function handleApprove() {
    await genehmigen({
      idAntrag: currentAntrag.id || '',
      bemerkungVorgesetzter: form.getValues('bemerkungVorgesetzter') || '',
      idManagerVorgesetzter: manager.id,
    });
    setIsUpdating(false);
  }

  async function handleReject() {
    await ablehnen({
      idAntrag: currentAntrag.id || '',
      bemerkungVorgesetzter: form.getValues('bemerkungVorgesetzter') || '',
      idManagerVorgesetzter: manager.id,
    });
    setIsUpdating(false);
  }
  const isBeginnChangeRequested = currentAntrag.beginn?.isChangeRequested;
  const isEndeChangeRequested = currentAntrag?.ende?.isChangeRequested;
  const hasBeginnTime = Boolean(currentAntrag.beginn?.time);
  const hasEndeTime = Boolean(currentAntrag.ende?.time);

  const getFieldClass = (
    isChangeRequested: boolean | undefined,
    otherChangeRequested: boolean | undefined,
    hasTime: boolean,
    otherHasTime: boolean,
  ) => {
    if (
      (isChangeRequested && !otherChangeRequested) ||
      (hasTime && otherHasTime && isChangeRequested) ||
      (hasTime &&
        otherHasTime &&
        !isChangeRequested &&
        !otherChangeRequested) ||
      (hasTime && !otherHasTime)
    ) {
      return 'bg-primary-blue-100 border border-primary-blue-700 text-primary-blue-700';
    }
    return '';
  };

  const beginnFieldClass = getFieldClass(
    isBeginnChangeRequested,
    isEndeChangeRequested,
    hasBeginnTime,
    hasEndeTime,
  );

  const endeFieldClass = getFieldClass(
    isEndeChangeRequested,
    isBeginnChangeRequested,
    hasEndeTime,
    hasBeginnTime,
  );

  return (
    <Form {...form}>
      {currentAntrag.offen && (
        <>
          <form
            id="createArbeitszeit"
            className="grow flex flex-col justify-start items-center gap-6"
            onSubmit={form.handleSubmit(({ beginn, ende }) => {
              handleSubmit(beginn, ende);
            })}>
            <div className="w-full px-2 md:w-[95%]">
              <FormField
                control={form.control}
                name="bemerkungAntragsteller"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex flex-col mb-2 rounded-(--xs,6px) shadow-[0px_4px_8px_0px_rgba(0,0,0,0.14),0px_0px_2px_0px_rgba(0,0,0,0.12)] px-3 pt-4 pb-2 m-2">
                        <AntragInfoText
                          beginn={currentAntrag.beginn}
                          ende={currentAntrag.ende}
                          aenderungsdatum={currentAntrag.aenderungsdatum}
                          offen={currentAntrag.offen}
                        />
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-2 text-neutrals-700 font-normal text-sm ps-6">
                            {renderAvatar(
                              currentAntrag.mitarbeiter.bildUrl,
                              currentAntrag.mitarbeiter.vorname,
                              currentAntrag.mitarbeiter.nachname,
                            )}
                            <span>{field.value}</span>
                          </div>
                        </div>
                      </div>
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="w-full px-4 md:w-[400px]">
              <FormField
                disabled
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('domain.arbeitszeiten.antrag.datum')}
                    </FormLabel>
                    <FormControl>
                      <div className="flex items-start text-sm">
                        {field.value
                          ? dayjs(field.value)
                              .locale('de')
                              .format('dd., D. MMMM')
                          : ''}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="w-full md:w-[400px] px-4 flex justify-between items-start gap-6">
              <FormField
                control={form.control}
                name="beginn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('domain.arbeitszeiten.sheet.arbeitszeit.beginnLabel')}
                    </FormLabel>
                    <FormControl>
                      <TimeInput
                        className={`font-bold w-28 ${beginnFieldClass}`}
                        readOnly
                        disabled={true}
                        placeholder="--:--"
                        defaultValue={field.value}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="h-px bg-neutrals-500 w-32 mt-10" />
              <FormField
                control={form.control}
                name="ende"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('domain.arbeitszeiten.sheet.arbeitszeit.endeLabel')}
                    </FormLabel>
                    <FormControl>
                      <TimeInput
                        className={`font-bold w-28 ${endeFieldClass}`}
                        readOnly
                        disabled={true}
                        placeholder="--:--"
                        defaultValue={field.value}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {canEditArbeitszeit && (
              <div className="w-full px-4 md:w-[400px]">
                <FormField
                  control={form.control}
                  name="bemerkungVorgesetzter"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t('domain.arbeitszeiten.antrag.bemerkungLabel')}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          className="border border-neutrals-300 ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 outline-hidden focus:outline-hidden shadow-none"
                          value={field.value}
                          maxLength={250}
                          onChange={field.onChange}
                          ref={field.ref}
                          disabled={currentAntrag.bemerkungVorgesetzter !== ''}
                        />
                      </FormControl>
                      <FormDescription />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
            <div className="w-full flex justify-center gap-6 items-center text-sm">
              {(currentAntrag.status as ArbeitszeitArbeitszeitantragStatus) ===
              ArbeitszeitArbeitszeitantragStatus.Genehmigt ? (
                <span className="flex items-center gap-2 px-3 py-1 bg-primary-green-100 text-primary-green-700 font-semibold rounded-full">
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="text-primary-green-700"
                  />
                  {t('domain.arbeitszeiten.antrag.genehmigt')}
                </span>
              ) : currentAntrag.status ===
                ArbeitszeitArbeitszeitantragStatus.Abgelehnt ? (
                <span className="flex items-center gap-2 px-3 py-1 bg-primary-red-100 text-primary-red-700 font-semibold rounded-full">
                  <FontAwesomeIcon
                    icon={faX}
                    className="text-primary-red-700"
                  />
                  {t('domain.arbeitszeiten.antrag.abgelehnt')}
                </span>
              ) : canEditArbeitszeit ? (
                <>
                  <Button
                    type="button"
                    disabled={isUpdating || antraegeLoading}
                    onClick={() => handleReject()}
                    className="flex items-center rounded-lg bg-primary-red-400 hover:bg-primary-red-400 text-neutrals-100 border-transparent hover:font-semibold hover:text-naturals-N900 w-[118px] gap-1">
                    <FontAwesomeIcon
                      icon={isUpdating ? faSpinner : faX}
                      spin={isUpdating}
                      size={'sm'}
                    />

                    <span className="px-1">
                      {t('domain.arbeitszeiten.antrag.ablehnen')}
                    </span>
                  </Button>
                  <Button
                    type="button"
                    disabled={isUpdating || antraegeLoading}
                    onClick={() => handleApprove()}
                    className="flex items-center rounded-lg bg-primary-blue-600 hover:bg-primary-blue-600 text-neutrals-100 border-transparent hover:font-semibold hover:text-naturals-N900 w-32 gap-1">
                    <FontAwesomeIcon
                      icon={isUpdating ? faSpinner : faCheck}
                      spin={isUpdating}
                      size={'lg'}
                    />

                    <span className="px-1 ">
                      {t('domain.arbeitszeiten.antrag.annehmen')}
                    </span>
                  </Button>
                </>
              ) : (
                <></>
              )}
            </div>
          </form>
        </>
      )}

      <AntragVerlauf
        antraege={antraege}
        currentAntrag={currentAntrag}
        beginnAZ={beginnAZ}
        endeAZ={endeAZ}
        initialsVorgesetzter={initialsVorgesetzter}
      />
    </Form>
  );
}
